import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
        config.headers.Authorization = `Token ${token}`;
    }
    return config;
});

/**
 * Window event fired when the server rejects the stored token. The app shell
 * listens for it to drop its auth state and show the login form again.
 */
export const AUTH_EXPIRED_EVENT = 'al:auth-expired';

/** The localStorage keys that together make up a signed-in session. */
const AUTH_STORAGE_KEYS = ['authToken', 'username', 'userId', 'isStaff', 'isModerator', 'isContentCreator'];

/** Forget the signed-in session. Safe to call when storage is unavailable. */
export function clearAuthStorage() {
    AUTH_STORAGE_KEYS.forEach((key) => {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            // Private mode / disabled storage: nothing to clear.
        }
    });
}

// Signing in and registering answer 401/403 on bad credentials. Treating those
// as an expired session would clear storage mid-login and re-fire the event on
// every retry, so they are excluded.
const AUTH_ENDPOINTS = ['/login/', '/register/'];

/**
 * Does this error response mean "your token is no longer good"? 401 always
 * does; DRF also answers 403 with an explanatory detail for a stale or missing
 * token, which is indistinguishable from a real permission error without it.
 */
const isExpiredSession = (response) => {
    if (!response) return false;
    if (response.status === 401) return true;
    if (response.status !== 403) return false;
    const detail = String(response.data?.detail || '');
    return /invalid token/i.test(detail) || /authentication credentials/i.test(detail);
};

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const url = error?.config?.url || '';
        const isAuthEndpoint = AUTH_ENDPOINTS.some((path) => url.includes(path));
        let hadToken = false;
        try {
            hadToken = !!localStorage.getItem('authToken');
        } catch (e) {
            hadToken = false;
        }

        if (hadToken && !isAuthEndpoint && isExpiredSession(error?.response)) {
            clearAuthStorage();
            window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
        }
        return Promise.reject(error);
    }
);

// Some moderator endpoints accept either JSON or a multipart FormData payload
// (e.g. an optional reference-image upload). When FormData is passed we must set
// the multipart content type so the default JSON transform doesn't serialize it.
const isFormData = (data) => typeof FormData !== 'undefined' && data instanceof FormData;
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export const authAPI = {
    login: (data) => api.post('/login/', data),
    register: (data) => api.post('/register/', data),
    getSystemStatus: () => api.get('/system-status/'),
    // Backwards compatibility
    maintenanceStatus: () => api.get('/maintenance-status/'),
};

export const cardAPI = {
    getCards: () => api.get('/cards/'),
    getMyCards: (params) => api.get('/cards/my_cards/', { params }),
    getCard: (id) => api.get(`/cards/${id}/`),
    getCardDates: (cardId, params) => api.get(`/cards/${cardId}/dates/`, { params }),
    getCardVideos: (cardId, params) => api.get(`/cards/${cardId}/videos/`, { params }),
    setActiveDate: (cardId, dateId) => api.post(`/cards/${cardId}/set_active_date/`, { date_id: dateId }),
    recycleCard: (cardId, rewardType) => api.post(`/cards/${cardId}/recycle/`, { reward_type: rewardType }),
    bulkRecycleCards: (cardIds, rewardType) => api.post('/cards/bulk_recycle/', { card_ids: cardIds, reward_type: rewardType }),
    mutateCard: (cardId, mutationItemId, hairColorToReplace = null, eyeColorToReplace = null, skipDateGeneration = false) => {
        const data = { mutation_item_id: mutationItemId, skip_date_generation: skipDateGeneration };
        if (hairColorToReplace) {
            data.hair_color_to_replace = hairColorToReplace;
        }
        if (eyeColorToReplace) {
            data.eye_color_to_replace = eyeColorToReplace;
        }
        return api.post(`/cards/${cardId}/mutate/`, data);
    },
    deleteUniqueCharacteristic: (cardId, characteristicId) => api.post(`/cards/${cardId}/delete_unique/`, { characteristic_id: characteristicId }),
    generateShareImage: (cardId, dateId = null) => api.post(`/cards/${cardId}/generate_share_image/`, { date_id: dateId }),
    renameCard: (cardId, newName) => api.post(`/cards/${cardId}/rename/`, { name: newName }),
    lockCard: (cardId) => api.post(`/cards/${cardId}/lock/`),
    unlockCard: (cardId) => api.post(`/cards/${cardId}/unlock/`),
    getStats: (includeZeroCounts = false, excludeLevel1 = false) => api.get('/cards/stats/', { params: { ...(includeZeroCounts ? { include_zero_counts: 'true' } : {}), ...(excludeLevel1 ? { exclude_level_1: 'true' } : {}) } }),
};

export const dateAPI = {
    getDates: () => api.get('/dates/'),
    getDate: (id) => api.get(`/dates/${id}/`),
    getMyDates: () => api.get('/dates/my_dates/'),
    getCompetitionEligibleDates: (params) => api.get('/dates/competition_eligible/', { params }),
    // data: { card_id, wish_ids, style_id?, orientation? } — orientation is
    // 'portrait' (default) or 'landscape'; landscape is rejected (400) when the
    // selection includes an img2img wish.
    createDate: (data) => api.post('/dates/create_date/', data),
    deleteDate: (id) => api.delete(`/dates/${id}/`),
    bulkDelete: (dateIds) => api.post('/dates/bulk-delete/', { date_ids: dateIds }),
    browseDates: (params) => api.get('/dates/browse/', { params }),
    browseThemes: (params) => api.get('/dates/browse-themes/', { params }),
    toggleFavorite: (id) => api.post(`/dates/${id}/favorite/`),
    // method: optional key from the date's upscale_methods (e.g. 'fast', 'detailed')
    upscaleDate: (id, method) => api.post(`/dates/${id}/upscale/`, method ? { method } : {}),
    prepareDownload: (dateIds) => api.post('/dates/prepare-download/', { date_ids: dateIds }),
    downloadZip: (downloadId) => api.get(`/dates/download/?download_id=${downloadId}`, { responseType: 'blob' }),
};

export const wishAPI = {
    getWishes: (params) => api.get('/wishes/', { params }),
    getMyWishes: () => api.get('/wishes/my_wishes/'),
    addWishToUser: (wishId, userId, amount) => api.post(`/wishes/${wishId}/add_to_user/`, { user_id: userId, amount }),
    bulkAddWishesToUser: (userId, amount) => api.post('/wishes/bulk_add_to_user/', { user_id: userId, amount }),
    // Moderator endpoints
    getModeratorList: (includeDeleted = false) => api.get('/wishes/moderator_list/', { params: { include_deleted: includeDeleted } }),
    createWish: (data) => (isFormData(data) ? api.post('/wishes/', data, MULTIPART) : api.post('/wishes/', data)),
    updateWish: (id, data) => (isFormData(data) ? api.put(`/wishes/${id}/`, data, MULTIPART) : api.put(`/wishes/${id}/`, data)),
    patchWish: (id, data) => api.patch(`/wishes/${id}/`, data),
    deleteWish: (id) => api.delete(`/wishes/${id}/`),
    bulkUpdateStatus: (ids, status) => api.post('/wishes/bulk_update_status/', { ids, status }),
    // Refile a whole selection into one category without opening each wish.
    bulkUpdateCategory: (ids, category) => api.post('/wishes/bulk_update_category/', { ids, category }),
    importJson: (wishesData) => api.post('/wishes/import_json/', { wishes_data: wishesData }),
    // Returns 202 + {job_id}; the image itself is polled via getExampleStatus.
    generateExample: (wishId) => api.post(`/wishes/${wishId}/generate_example/`),
    getWishExamples: (params) => api.get('/wishes/examples/', { params }),
    getExampleStatus: (wishId, jobId) => api.get(`/wishes/${wishId}/example-status/`, { params: { job_id: jobId } }),
};

/** @deprecated Use wishAPI; kept so existing call sites keep working. */
export const wishesAPI = wishAPI;

export const styleAPI = {
    // Player-facing list returns active styles only (backend-gated).
    getStyles: (params) => api.get('/styles/', { params }),
    // Moderators pass ?status=all to include drafts/review/deleted.
    getModeratorList: (status = 'all') => api.get('/styles/', { params: { status } }),
    createStyle: (data) => api.post('/styles/', data),
    updateStyle: (id, data) => api.put(`/styles/${id}/`, data),
    patchStyle: (id, data) => api.patch(`/styles/${id}/`, data),
    deleteStyle: (id) => api.delete(`/styles/${id}/`),
    bulkUpdateStatus: (ids, status) => api.post('/styles/bulk_update_status/', { ids, status }),
    // Toggles the current user's favorite flag; returns { id, is_favorite }.
    toggleFavorite: (id) => api.post(`/styles/${id}/favorite/`),
};

export const styleReferenceAPI = {
    // Flat list of reference images (newest first); frontend groups by style.
    getReferences: () => api.get('/style-references/'),
    // Moderator-only: copy a completed test date's image as a style reference.
    addFromDate: (dateId) => api.post('/style-references/from-date/', { date_id: dateId }),
    // Moderator-only.
    deleteReference: (id) => api.delete(`/style-references/${id}/`),
};

export const profileAPI = {
    getMyProfile: () => api.get('/profiles/me/'),
    updateProfile: (data) => api.patch('/profiles/me/', data),
    updateCardSort: (sortBy) => api.post('/profiles/update_card_sort/', { sort_by: sortBy }),
    getWishPreferences: () => api.get('/profiles/wish_preferences/'),
    updateWishPreferences: (disabledWishes) => api.post('/profiles/wish_preferences/', { disabled_random_wishes: disabledWishes }),
    changePassword: (currentPassword, newPassword) => api.post('/profiles/change_password/', { current_password: currentPassword, new_password: newPassword }),
    deleteAccount: (password) => api.post('/profiles/delete_account/', { password }),
    getDiscordLinkUrl: () => api.get('/profiles/discord_link/'),
    linkDiscord: (code) => api.post('/profiles/discord_callback/', { code }),
    unlinkDiscord: () => api.post('/profiles/discord_unlink/'),
};

export const userAPI = {
    getUsers: (params) => api.get('/users/', { params }),
};

export const themeAPI = {
    getThemes: (params) => api.get('/themes/', { params }),
    searchThemes: (query, limit = 10) => api.get('/themes/search/', { params: { q: query, limit } }),
    getThemeCharacteristics: (themeId) => api.get(`/themes/${themeId}/characteristics/`),
    createTheme: (data) => api.post('/themes/', data),
    updateTheme: (id, data) => api.put(`/themes/${id}/`, data),
    deleteTheme: (id) => api.delete(`/themes/${id}/`),
    bulkUpdateStatus: (ids, status) => api.post('/themes/bulk_update_status/', { ids, status }),
    // Bulk edit ways-to-obtain. `payload` is {channels?, mode?: 'add'|'remove'|'replace', is_unique?}.
    bulkUpdateAcquisition: (ids, payload) => api.post('/themes/bulk_update_acquisition/', { ids, ...payload }),
    // Returns 202 + {job_id}; the image itself is polled via getExampleStatus.
    generateExample: (themeId) => api.post(`/themes/${themeId}/generate_example/`),
    getExampleStatus: (themeId, jobId) => api.get(`/themes/${themeId}/example-status/`, { params: { job_id: jobId } }),
    // Per-channel rules: what obtaining a theme through each channel allows.
    getAcquisitionChannelRules: () => api.get('/acquisition-channel-rules/'),
    updateAcquisitionChannelRules: (rules) => api.post('/acquisition-channel-rules/', { rules }),
    // state is one of 'compatible' | 'incompatible' | 'unknown' (unknown clears the row).
    setGeneratorCompatibility: (themeId, generatorId, state) =>
        api.post(`/themes/${themeId}/set_generator_compatibility/`, { generator_id: generatorId, state }),
    getThemeExamples: (params) => api.get('/themes/examples/', { params }),
};

/** @deprecated Use themeAPI; kept so existing call sites keep working. */
export const themesAPI = themeAPI;

export const announcementAPI = {
    getAnnouncements: (params) => api.get('/announcements/', { params }),
    // Player-facing feed shown on the home page.
    getPublished: () => api.get('/announcements/published/'),
    getAnnouncement: (id) => api.get(`/announcements/${id}/`),
    createAnnouncement: (data) => api.post('/announcements/', data),
    updateAnnouncement: (id, data) => api.put(`/announcements/${id}/`, data),
    deleteAnnouncement: (id) => api.delete(`/announcements/${id}/`),
    publishAnnouncement: (id) => api.post(`/announcements/${id}/publish/`, {}),
    unpublishAnnouncement: (id) => api.post(`/announcements/${id}/unpublish/`, {}),
    transferSection: (id, sectionId, targetAnnouncementId) =>
        api.post(`/announcements/${id}/transfer-section/`, { section_id: sectionId, target_announcement_id: targetAnnouncementId }),
};

export const achievementAPI = {
    getAchievements: (params) => api.get('/achievements/', { params }),
    // Player-facing: the catalog merged with the caller's progress/claim state.
    getMyAchievements: () => api.get('/achievements/mine/'),
    claimAchievement: (id) => api.post(`/achievements/${id}/claim/`),
    createAchievement: (data) => api.post('/achievements/', data),
    updateAchievement: (id, data) => api.put(`/achievements/${id}/`, data),
    deleteAchievement: (id) => api.delete(`/achievements/${id}/`),
    // Persist a drag-reordered category: `ids` in their new display order.
    reorderAchievements: (ids) => api.post('/achievements/reorder/', { ids }),
};

export const giftAPI = {
    getGifts: (params) => api.get('/gifts/', { params }),
    // Gifts waiting for the current player, and claiming one.
    getMyGifts: () => api.get('/gifts/my_gifts/'),
    claimGift: (id) => api.post(`/gifts/${id}/claim/`, {}),
    createGift: (data) => api.post('/gifts/', data),
    updateGift: (id, data) => api.patch(`/gifts/${id}/`, data),
    deleteGift: (id) => api.delete(`/gifts/${id}/`),
    getGiftHistory: (params) => api.get('/gift-history/', { params }),
};

export const boosterTypeAPI = {
    getBoosterTypes: (params) => api.get('/booster-types/', { params }),
    createBoosterType: (data) => (isFormData(data) ? api.post('/booster-types/', data, MULTIPART) : api.post('/booster-types/', data)),
    updateBoosterType: (id, data) => (isFormData(data) ? api.patch(`/booster-types/${id}/`, data, MULTIPART) : api.patch(`/booster-types/${id}/`, data)),
    deleteBoosterType: (id) => api.delete(`/booster-types/${id}/`),
    // Moderator sample opening: same rolls as a real one, nothing granted
    simulateBoosterOpening: (id) => api.post(`/booster-types/${id}/simulate_open/`),
};

export const boosterAPI = {
    getMyBoosters: () => api.get('/my-boosters/'),
    openBooster: (id) => api.post(`/my-boosters/${id}/open/`),
};

export const generatorAPI = {
    getGenerators: () => api.get('/generators/'),
    createGenerator: (data) => api.post('/generators/', data),
    updateGenerator: (id, data) => api.patch(`/generators/${id}/`, data),
    deleteGenerator: (id) => api.delete(`/generators/${id}/`),
};

export const themeCategoryAPI = {
    getCategories: () => api.get('/theme-categories/'),
    createCategory: (data) => api.post('/theme-categories/', data),
    updateCategory: (id, data) => api.patch(`/theme-categories/${id}/`, data),
    deleteCategory: (id) => api.delete(`/theme-categories/${id}/`),
};

// Shop/reward channels that dispense acquisition-restricted ("exclusive") themes.
export const shopCollectionAPI = {
    getCollections: () => api.get('/shop-collections/'),
    createCollection: (data) => api.post('/shop-collections/', data),
    updateCollection: (id, data) => api.patch(`/shop-collections/${id}/`, data),
    deleteCollection: (id) => api.delete(`/shop-collections/${id}/`),
    // Every shop-dispensed or restricted theme with its current collection assignment.
    getExclusiveThemes: () => api.get('/shop-collections/exclusive-themes/'),
    setTheme: (collectionId, themeId) =>
        api.post(`/shop-collections/${collectionId}/set-theme/`, { theme_id: themeId }),
    unsetTheme: (themeId) =>
        api.post('/shop-collections/unset-theme/', { theme_id: themeId }),
    // File every exclusive theme into the collection that claims its category.
    autoFile: () => api.post('/shop-collections/auto-file/'),
};

// Staff-managed categories for the wish / characteristic / video wish menus.
// `scope` is one of 'wish' | 'characteristic' | 'video_wish'.
export const contentCategoryAPI = {
    getCategories: (scope) =>
        api.get('/content-categories/', { params: scope ? { scope } : {} }),
    createCategory: (data) => api.post('/content-categories/', data),
    updateCategory: (id, data) => api.patch(`/content-categories/${id}/`, data),
    deleteCategory: (id) => api.delete(`/content-categories/${id}/`),
    // `ids` in their new display order, within one parent's sibling list.
    reorderCategories: (ids) => api.post('/content-categories/reorder/', { ids }),
    // { scope: { slug: count } } — how much content sits in each category.
    getUsage: () => api.get('/content-categories/usage/'),
};

export const zodiacAbilityAPI = {
    getConfig: () => api.get('/zodiac-abilities/'),
    createConfig: (data) => api.post('/zodiac-abilities/', data),
    updateConfig: (id, data) => api.patch(`/zodiac-abilities/${id}/`, data),
    deleteConfig: (id) => api.delete(`/zodiac-abilities/${id}/`),
};

export const weeklyShopAdminAPI = {
    // week is a YYYY-MM-DD date; the backend snaps it back to that week's Monday.
    getWeek: (week) => api.get('/weekly-shop-admin/', { params: week ? { week } : {} }),
    setWeek: (weekStart, offers) =>
        api.post('/weekly-shop-admin/', { week_start: weekStart, offers }),
    // The themes the shop may stock — released, Zodiac category excluded.
    getThemes: () => api.get('/weekly-shop-admin/themes/'),
};

export const divinationLogsAdminAPI = {
    // params: { username, event_type, limit, offset } — all optional.
    getLogs: (params) => api.get('/divination-logs-admin/', { params }),
};

export const tradeAPI = {
    getTrades: (params) => api.get('/trades/', { params }),
    getMyTrades: () => api.get('/trades/my_trades/'),
    createTrade: (cardIds, lookingFor = '') => api.post('/trades/', { card_ids: cardIds, looking_for: lookingFor }),
    deleteTrade: (id) => api.delete(`/trades/${id}/`),
    getTradeProposals: (tradeId) => api.get(`/trades/${tradeId}/proposals/`),
    acceptProposal: (tradeId, proposalId) => api.post(`/trades/${tradeId}/accept_proposal/`, { proposal_id: proposalId }),
    hideTrade: (tradeId) => api.post(`/trades/${tradeId}/hide/`),
    unhideTrade: (tradeId) => api.post(`/trades/${tradeId}/unhide/`),
    getHiddenTrades: () => api.get('/trades/hidden_trades/'),
    getPendingProposalsCount: () => api.get('/trades/pending_proposals_count/'),
    // Proposals a player made on someone else's trade.
    getMyProposals: () => api.get('/trade-proposals/my_proposals/'),
    createProposal: (tradeId, offeredCardId) => api.post('/trade-proposals/', { trade_id: tradeId, offered_card_id: offeredCardId }),
    deleteProposal: (id) => api.delete(`/trade-proposals/${id}/`),
};

/** @deprecated Use tradeAPI; kept so existing call sites keep working. */
export const tradeProposalAPI = tradeAPI;

export const wishPresetAPI = {
    getPresets: () => api.get('/wish-presets/'),
    createPreset: (name, wishIds, styleId = null, thumbnailDateId = null) =>
        api.post('/wish-presets/', { name, wish_ids: wishIds, style_id: styleId, thumbnail_date_id: thumbnailDateId }),
    updatePreset: (id, name, wishIds, styleId = null) => api.put(`/wish-presets/${id}/`, { name, wish_ids: wishIds, style_id: styleId }),
    // thumbnailDateId is a date hashid; null reverts to the wish-derived thumbnail.
    setPresetThumbnail: (id, thumbnailDateId) => api.patch(`/wish-presets/${id}/`, { thumbnail_date_id: thumbnailDateId }),
    deletePreset: (id) => api.delete(`/wish-presets/${id}/`),
};

export const publishedPresetAPI = {
    getPresets: (params) => api.get('/published-presets/', { params }),
    getPreset: (id) => api.get(`/published-presets/${id}/`),
    publishPreset: (dateId, name, description, includeStyle = true) => api.post('/published-presets/', { date_id: dateId, name, description, include_style: includeStyle }),
    deletePreset: (id) => api.delete(`/published-presets/${id}/`),
    getMyPublished: () => api.get('/published-presets/my_published/'),
    likePreset: (id) => api.post(`/published-presets/${id}/like/`),
    saveAsPreset: (id, name) => api.post(`/published-presets/${id}/save_as_preset/`, { name }),
};

export const galleryAPI = {
    getMyGalleries: (params = {}) => api.get('/galleries/', { params }),
    getSharedGallery: (hashid) => api.get(`/galleries/shared/${hashid}/`),
    getItems: (id, params = {}) => api.get(`/galleries/${id}/items/`, { params }),
    createGallery: (data) => api.post('/galleries/', data),
    updateGallery: (id, data) => api.patch(`/galleries/${id}/`, data),
    deleteGallery: (id) => api.delete(`/galleries/${id}/`),
    addDate: (id, dateId) => api.post(`/galleries/${id}/add_date/`, { date_id: dateId }),
    removeDate: (id, itemId) => api.post(`/galleries/${id}/remove_date/`, { item_id: itemId }),
    reorderItems: (id, itemIds) => api.post(`/galleries/${id}/reorder/`, { item_ids: itemIds }),
    publish: (id) => api.post(`/galleries/${id}/publish/`),
    unpublish: (id) => api.post(`/galleries/${id}/unpublish/`),
    likeItem: (itemId) => api.post(`/gallery-items/${itemId}/like/`),
    copyWishes: (itemId, name) => api.post(`/gallery-items/${itemId}/copy_wishes/`, { name }),
};

export const moderatorWishPresetAPI = {
    getPresets: () => api.get('/moderator-wish-presets/'),
    createPreset: (name, wishIds, description = '') => api.post('/moderator-wish-presets/', { name, wish_ids: wishIds, description }),
    updatePreset: (id, name, wishIds, description = '') => api.put(`/moderator-wish-presets/${id}/`, { name, wish_ids: wishIds, description }),
    deletePreset: (id) => api.delete(`/moderator-wish-presets/${id}/`),
};

export const wishSettingsPresetAPI = {
    getPresets: () => api.get('/wish-settings-presets/'),
    createPreset: (name, wishIds, styleId = null) => api.post('/wish-settings-presets/', { name, wish_ids: wishIds, style_id: styleId }),
    updatePreset: (id, name, wishIds, styleId = null) => api.put(`/wish-settings-presets/${id}/`, { name, wish_ids: wishIds, style_id: styleId }),
    deletePreset: (id) => api.delete(`/wish-settings-presets/${id}/`),
};

// New Features APIs
export const tokensAPI = {
    getMyTokens: () => api.get('/tokens/me/'),
    getRarityCosts: () => api.get('/tokens/rarity_costs/'),
    generateCardWithTokens: (type, themeId = null, skipFirstDate = false) => api.post('/tokens/generate_card/', { type, theme_id: themeId, skip_first_date: skipFirstDate }),
    generateCardBatch: (type, themeId = null, quantity = 1, skipFirstDate = false, firstDatePresetId = null) => api.post('/tokens/generate_card_batch/', { type, theme_id: themeId, quantity, skip_first_date: skipFirstDate, first_date_preset_id: firstDatePresetId }),
    generateCardWithRandom: (rarity, skipFirstDate = false) => api.post('/tokens/generate_card_with_random/', { rarity, skip_first_date: skipFirstDate }),
    transformMutations: (mutationItemIds) => api.post('/tokens/transform_mutations/', { mutation_item_ids: mutationItemIds }),
    transmuteToSpecific: (mutationItemIds, targetItemId) => api.post('/tokens/transmute_to_specific/', { mutation_item_ids: mutationItemIds, target_item_id: targetItemId }),
    convertThemeToRandom: (themeId, amount) => api.post('/tokens/convert_theme_to_random/', { theme_id: themeId, amount }),
};

export const expeditionAPI = {
    getTemplates: () => api.get('/expedition-templates/'),
    getTemplate: (id) => api.get(`/expedition-templates/${id}/`),
    getSuitableTemplates: (cardId) => api.get(`/expedition-templates/suitable_for_card/${cardId}/`),
    getMyExpeditions: () => api.get('/expeditions/my_expeditions/'),
    // Badge counts only: { ready_to_collect }. Cheaper than listing every
    // expedition just to count the claimable ones.
    getCounts: () => api.get('/expeditions/counts/'),
    startExpedition: (payload) => api.post('/expeditions/start/', payload),
    claimRewards: (expeditionId, payload = {}) => api.post(`/expeditions/${expeditionId}/claim_rewards/`, payload),
    claimAllRewards: () => api.post('/expeditions/claim_all_rewards/'),
    cancelExpedition: (expeditionId) => api.post(`/expeditions/${expeditionId}/cancel/`),
    getHistory: () => api.get('/expeditions/history/'),
};

export const minigameAPI = {
    startGame: () => api.get('/guess-wish-game/start/'),
    submitGuess: (gameId, selectedWishId) => api.post(`/guess-wish-game/${gameId}/submit/`, { selected_wish_id: selectedWishId }),
    getStats: () => api.get('/guess-wish-game/stats/'),
    getHistory: () => api.get('/guess-wish-game/history/'),
};

export const divinationAPI = {
    getState: () => api.get('/divination/state/'),
    completeTutorial: () => api.post('/divination/complete_tutorial/'),
    completeLesson: (lesson) => api.post('/divination/complete_lesson/', { lesson }),
    completeCodexLesson: (key) => api.post('/divination/complete_codex_lesson/', { key }),
    start: (spreadType = 'spirit') => api.post('/divination/start/', { spread_type: spreadType }),
    place: (runId, placement, playerCard = null) => api.post(`/divination/${runId}/place/`, playerCard ? { placement, player_card: playerCard } : { placement }),
    cancel: (runId) => api.post(`/divination/${runId}/cancel/`),
    flip: (runId, slot, prediction) => api.post(`/divination/${runId}/flip/`, { slot, prediction }),
    ability: (runId, slot, { target = null, targets = null, option = null, painted = null, abilityKey = null } = {}) =>
        api.post(`/divination/${runId}/ability/`, {
            slot,
            ...(target != null ? { target } : {}),
            ...(targets != null ? { targets } : {}),
            ...(option != null ? { option } : {}),
            ...(painted != null ? { painted } : {}),
            ...(abilityKey != null ? { ability_key: abilityKey } : {}),
        }),
    choose: (runId, choice) => api.post(`/divination/${runId}/choose/`, { choice }),
    endRun: (runId) => api.post(`/divination/${runId}/end_run/`),
    getShop: () => api.get('/divination/shop/'),
    buy: (sign) => api.post('/divination/buy/', { sign }),
    buyElement: (element) => api.post('/divination/buy_element/', { element }),
    getWeekly: () => api.get('/divination/weekly/'),
    buyWeekly: (offer) => api.post('/divination/buy_weekly/', { offer }),
    getHistory: (limit = 20, offset = 0) => api.get('/divination/history/', { params: { limit, offset } }),
};

export const competitionAPI = {
    getCompetitions: () => api.get('/competitions/'),
    getCurrentCompetition: () => api.get('/competitions/current/'),
    getCompetition: (id) => api.get(`/competitions/${id}/`),
    getEntries: (competitionId, showHidden = false) => api.get(`/competitions/${competitionId}/entries/`, { params: { show_hidden: showHidden } }),
    getLeaderboard: (competitionId) => api.get(`/competitions/${competitionId}/leaderboard/`),
    getMyEntries: () => api.get('/competition-entries/my_entries/'),
    getWinners: (page = 1, pageSize = 20) => api.get('/competition-entries/winners/', { params: { page, page_size: pageSize } }),
    submitEntry: (competitionId, dateId) => api.post('/competition-entries/', { competition_id: competitionId, date_id: dateId }),
    withdrawEntry: (entryId) => api.delete(`/competition-entries/${entryId}/`),
    hideEntry: (entryId) => api.post(`/competition-entries/${entryId}/hide/`),
    unhideEntry: (entryId) => api.post(`/competition-entries/${entryId}/unhide/`),
    claimRewards: (entryId, themeSelections = null, mutationSelections = null) => api.post(`/competition-entries/${entryId}/claim_rewards/`, { theme_selections: themeSelections, mutation_selections: mutationSelections }),
    disqualifyEntry: (entryId, reason) => api.post(`/competition-entries/${entryId}/disqualify/`, { reason }),
    restoreEntry: (entryId) => api.post(`/competition-entries/${entryId}/restore/`),
    getMyVotes: () => api.get('/competition-votes/my_votes/'),
    vote: (competitionId, entryId) => api.post('/competition-votes/', { competition_id: competitionId, entry_id: entryId }),
    deleteVote: (voteId) => api.delete(`/competition-votes/${voteId}/`),
    // Competition Shop (Lumis)
    getShop: () => api.get('/competition-shop/'),
    buyShopOffer: (offerId) => api.post('/competition-shop/buy/', { offer_id: offerId }),
    getLumisHistory: () => api.get('/competition-shop/history/'),
    // Competition Shop (moderators)
    getShopManage: () => api.get('/competition-shop/manage/'),
    getShopEligibleThemes: () => api.get('/competition-shop/eligible_themes/'),
    getShopEligibleBoosters: () => api.get('/competition-shop/eligible_boosters/'),
    addShopOffer: (themeId) => api.post('/competition-shop/offers/', { theme_id: themeId }),
    addShopBoosterOffer: (boosterTypeId, price) => api.post('/competition-shop/offers/', { booster_type_id: boosterTypeId, price }),
    updateShopOffer: (offerId, data) => api.patch(`/competition-shop/${offerId}/`, data),
    updateShopSettings: (data) => api.patch('/competition-shop/settings/', data),
    // Admin only
    createCompetition: (data) => api.post('/competitions/', data),
    updateCompetition: (id, data) => api.patch(`/competitions/${id}/`, data),
    launchCompetition: (id) => api.post(`/competitions/${id}/launch/`),
    deleteCompetition: (id) => api.delete(`/competitions/${id}/`),
};

export const cardTagAPI = {
    getTags: () => api.get('/card-tags/'),
    createTag: (name, color) => api.post('/card-tags/', { name, color }),
    updateTag: (id, data) => api.patch(`/card-tags/${id}/`, data),
    deleteTag: (id) => api.delete(`/card-tags/${id}/`),
};

export const cardTagAssignmentAPI = {
    getAssignments: (params) => api.get('/card-tag-assignments/', { params }),
    assignTag: (cardId, tagId) => api.post('/card-tag-assignments/', { card: cardId, tag: tagId }),
    removeAssignment: (assignmentId) => api.delete(`/card-tag-assignments/${assignmentId}/`),
};

export const characteristicAPI = {
    getCharacteristics: (params = {}) => api.get('/characteristics/', { params }),
    getThemesForCharacteristic: (characteristicId) => api.get(`/characteristics/${characteristicId}/themes/`),
    setThemesForCharacteristic: (characteristicId, themeIds) => api.post(`/characteristics/${characteristicId}/set_themes/`, { theme_ids: themeIds }),
    createCharacteristic: (data) => api.post('/characteristics/', data),
    updateCharacteristic: (id, data) => api.put(`/characteristics/${id}/`, data),
    patchCharacteristic: (id, data) => api.patch(`/characteristics/${id}/`, data),
    deleteCharacteristic: (id) => api.delete(`/characteristics/${id}/`),
    bulkUpdateCategory: (ids, category) => api.post('/characteristics/bulk_update_category/', { ids, category }),
};

export const videoWishAPI = {
    getVideoWishes: (params) => api.get('/video-wishes/', { params }),
    createVideoWish: (data) => api.post('/video-wishes/', data),
    updateVideoWish: (id, data) => api.patch(`/video-wishes/${id}/`, data),
    deleteVideoWish: (id) => api.delete(`/video-wishes/${id}/`),
    bulkUpdateStatus: (ids, status) => api.post('/video-wishes/bulk_update_status/', { ids, status }),
    bulkUpdateCategory: (ids, category) => api.post('/video-wishes/bulk_update_category/', { ids, category }),
};

export const dateVideoAPI = {
    getDateVideos: (params) => api.get('/date-videos/', { params }),
    getMyVideos: (params) => api.get('/date-videos/my_videos/', { params }),
    // width/height omitted → the backend picks them from the date image's
    // orientation (portrait 520×720, landscape 720×520)
    generateVideo: (dateId, videoWishId, width = null, height = null, seed = null, isTest = false) =>
        api.post('/date-videos/generate/', {
            date_id: dateId,
            video_wish_id: videoWishId,
            ...(width && height && { width, height }),
            ...(seed && { seed }),
            ...(isTest && { is_test: true }),
        }),
    checkStatus: (videoId) => api.get(`/date-videos/${videoId}/check_status/`),
    deleteVideo: (videoId) => api.delete(`/date-videos/${videoId}/`),
};

export const monitoringAPI = {
    getHealthCheck: () => api.get('/health/'),
    getDetailedHealth: () => api.get('/health/detailed/'),
    getPerformanceMetrics: () => api.get('/monitoring/metrics/'),
    getMonitoringDashboard: () => api.get('/monitoring/dashboard/'),
    getSlowQueries: () => api.get('/monitoring/slow-queries/'),
    resetMetrics: () => api.post('/monitoring/reset/'),
};

export const statsAPI = {
    getGenerationTimeStats: (params) => api.get('/stats/generation-time/', { params }),
    getGenerationBreakdown: (params) => api.get('/stats/generation-breakdown/', { params }),
    getGameEconomy: (params) => api.get('/stats/game-economy/', { params }),
    searchGameEconomyPlayers: (search) => api.get('/stats/game-economy/players/', { params: { search } }),
    getUserStats: (params) => api.get('/stats/users/', { params }),
    getAvailableUsers: (params) => api.get('/stats/available-users/', { params }),
};

export const queueStatusAPI = {
    getImageQueueStatus: () => api.get('/queue-status/image/'),
    getVideoQueueStatus: () => api.get('/queue-status/video/'),
};

export const storeAPI = {
    getItems: () => api.get('/store/items/'),
    purchaseItem: (itemId, quantity = 1, selections = {}) => api.post('/store/purchase/', { item_id: itemId, quantity, selections }),
    getBalance: () => api.get('/store/balance/'),
    getItemSelections: (itemId) => api.get(`/store/items/${itemId}/selections/`),
};

export const adminAPI = {
    getShopStats: () => api.get('/admin/shop-stats/'),
    generateResetLink: (userId) => api.post('/admin/generate-reset-link/', { user_id: userId }),
    listUsers: () => api.get('/users/list/'),
    getSuspiciousAccounts: () => api.get('/admin/suspicious-accounts/'),
};

/**
 * The staff "Prod Proposals" screen reads from the *production* deployment
 * rather than from this instance, authenticating with a standalone sync token
 * instead of the signed-in user's token. It therefore cannot go through `api`
 * (different base URL, different credentials) — this is the one cross-host
 * client, kept here so no page has to reach for raw axios.
 */
export const PRODUCTION_API_URL = process.env.REACT_APP_PROD_API_URL || 'https://aesthetic-legends.ovh/api';
const PRODUCTION_SYNC_TOKEN = process.env.REACT_APP_PROD_SYNC_TOKEN || '';

export const productionSyncAPI = {
    /** False when REACT_APP_PROD_SYNC_TOKEN is unset, so the page can explain why. */
    isConfigured: () => Boolean(PRODUCTION_SYNC_TOKEN),
    getProposals: () => axios.get(`${PRODUCTION_API_URL}/proposals/sync/`, {
        headers: { Authorization: `Bearer ${PRODUCTION_SYNC_TOKEN}` },
    }),
};

export const syncAPI = {
    pullProductionData: (dryRun = false) => api.post('/sync/pull-production/', { dry_run: dryRun }),
    // items: { styles: [ids], wishes: [ids], characteristics: [ids], themes: [ids], style_reference_images: [ids] }
    pushToProduction: (items, dryRun = false) => api.post('/sync/push-production/', { items, dry_run: dryRun }),
};

export const passwordResetAPI = {
    validateToken: (uid, token) => api.post('/auth/validate-reset-token/', { uid, token }),
    confirmReset: (uid, token, newPassword) => api.post('/auth/reset-password-confirm/', { uid, token, new_password: newPassword }),
};

export const donationAPI = {
    createCheckout: (amount, currency = 'eur') => api.post('/donations/create-checkout/', { amount, currency }),
    getPublishableKey: () => api.get('/donations/publishable-key/'),
    getStats: () => api.get('/donations/stats/'),
    getHistory: () => api.get('/donations/history/'),
};

export const subscriptionAPI = {
    getStatus: () => api.get('/subscriptions/status/'),
    createCheckout: (tier = 'basic') => api.post('/subscriptions/create-checkout/', { tier }),
    cancel: () => api.post('/subscriptions/cancel/'),
    claimCard: (themeId) => api.post('/subscriptions/claim-card/', { theme_id: themeId }),
    claimVideoWishes: (count = 2) => api.post('/subscriptions/claim-video-wishes/', { count }),
    claimMutationItems: (characteristicIds) => api.post('/subscriptions/claim-mutation-items/', { characteristic_ids: characteristicIds }),
    claimSlot: () => api.post('/subscriptions/claim-slot/'),
    getBlacklist: () => api.get('/subscriptions/blacklist/'),
    setBlacklist: (themeIds) => api.post('/subscriptions/blacklist/set/', { theme_ids: themeIds }),
};

export const dateGenAPI = {
    getStatus: () => api.get('/date-gens/status/'),
    collectFragments: () => api.post('/date-gens/collect-fragments/'),
};

// Proposal APIs - factory for consistent proposal endpoints
const createProposalAPI = (basePath) => ({
    getList: (params) => api.get(`/${basePath}/`, { params }),
    getMyProposals: () => api.get(`/${basePath}/my_proposals/`),
    create: (formData) => api.post(`/${basePath}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id) => api.delete(`/${basePath}/${id}/`),
    approve: (id, payload) => api.post(`/${basePath}/${id}/approve/`, payload),
    reject: (id, payload) => api.post(`/${basePath}/${id}/reject/`, payload),
    setToPending: (id) => api.post(`/${basePath}/${id}/set_to_pending/`, {}),
    markAsDone: (id) => api.post(`/${basePath}/${id}/mark_as_done/`, {}),
});

export const taskAPI = {
    getBoard: (limit = 20) => api.get('/tasks/board/', { params: { limit } }),
    getBoardColumn: (status, offset = 0, limit = 20) => api.get('/tasks/board/column/', { params: { status, offset, limit } }),
    getCalendarTasks: (year, month) => api.get('/tasks/calendar/', { params: { year, month } }),
    getList: (params) => api.get('/tasks/', { params }),
    getTask: (id) => api.get(`/tasks/${id}/`),
    createTask: (data) => api.post('/tasks/', data),
    updateTask: (id, data) => api.patch(`/tasks/${id}/`, data),
    deleteTask: (id) => api.delete(`/tasks/${id}/`),
    moveTask: (id, data) => api.post(`/tasks/${id}/move/`, data),
    addComment: (id, content) => api.post(`/tasks/${id}/add_comment/`, { content }),
    deleteComment: (id, commentId) => api.post(`/tasks/${id}/delete_comment/`, { comment_id: commentId }),
    addAttachment: (id, formData) => api.post(`/tasks/${id}/add_attachment/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    deleteAttachment: (id, attachmentId) => api.post(`/tasks/${id}/delete_attachment/`, { attachment_id: attachmentId }),
    searchThemes: (q) => api.get('/tasks/search_themes/', { params: { q } }),
    searchWishes: (q) => api.get('/tasks/search_wishes/', { params: { q } }),
    searchVideoWishes: (q) => api.get('/tasks/search_video_wishes/', { params: { q } }),
    searchCharacteristics: (q) => api.get('/tasks/search_characteristics/', { params: { q } }),
    searchDates: (q) => api.get('/tasks/search_dates/', { params: { q } }),
    browseTestGirls: () => api.get('/tasks/browse_test_girls/'),
    browseGirlDates: (cardId) => api.get(`/tasks/browse_girl_dates/${cardId}/`),
    searchProposals: (q, type) => api.get('/tasks/search_proposals/', { params: { q, type } }),
    searchUsers: (q) => api.get('/tasks/search_users/', { params: { q } }),
};

export const testGirlsAPI = {
    getTestGirls: (params) => api.get('/test-girls/', { params }),
    getAssignees: () => api.get('/test-girls/assignees/'),
    getTestGirlDates: (cardId, params) => api.get(`/test-girls/${cardId}/dates/`, { params }),
    createTestGirl: (data) => api.post('/test-girls/create/', data),
    updateTestGirl: (cardId, data) => api.put(`/test-girls/${cardId}/`, data),
    assignTestGirl: (cardId, userId) => api.post(`/test-girls/${cardId}/assign/`, { user_id: userId }),
    shareTestGirl: (cardId) => api.post(`/test-girls/${cardId}/share/`),
    unassignTestGirl: (cardId) => api.post(`/test-girls/${cardId}/unassign/`),
    createTestDate: (data) => api.post('/test-dates/', data),
    bulkCreateTestDates: (data) => api.post('/test-dates/bulk-create/', data),
};

export const testGirlGroupAPI = {
    getGroups: () => api.get('/test-girl-groups/'),
    createGroup: (name, cardIds) => api.post('/test-girl-groups/', { name, card_ids: cardIds }),
    updateGroup: (id, data) => api.patch(`/test-girl-groups/${id}/`, data),
    deleteGroup: (id) => api.delete(`/test-girl-groups/${id}/`),
};

export const wishProposalAPI = createProposalAPI('wish-proposals');
export const themeProposalAPI = createProposalAPI('theme-proposals');
export const videoWishProposalAPI = createProposalAPI('video-wish-proposals');
export const featureProposalAPI = createProposalAPI('feature-proposals');
export const bugProposalAPI = createProposalAPI('bug-proposals');

export default api;
